// Preview section with image display and crop controls
import React from 'react';
import { Box, Flex, Text, Button, HStack, Center, Image as ChakraImage } from '@chakra-ui/react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Crop as CropIcon, Check, X, RotateCcw } from 'lucide-react';
import { ImageItem } from '../types';

interface PreviewSectionProps {
  currentImage: ImageItem;
  imageRef: React.RefObject<HTMLImageElement>;
  isCropping: boolean;
  crop: Crop | undefined;
  setCrop: (crop: Crop) => void;
  setCompletedCrop: (crop: PixelCrop) => void;
  completedCrop: PixelCrop | undefined;
  cropAspect: number | undefined;
  isApplyingCrop: boolean;
  isResetting: boolean;
  hasBeenCropped: boolean;
  onStartCropping: () => void;
  onCancelCrop: () => void;
  onApplyCrop: () => void;
  onSetCropAspectRatio: (ratio: number | undefined) => void;
  onResetCrop: () => void;
}

const ASPECT_RATIOS = [
  { label: 'Free', value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:2', value: 3 / 2 },
  { label: '2:1', value: 2 / 1 },
  { label: '5:8', value: 5 / 8 },
];

export const PreviewSection: React.FC<PreviewSectionProps> = ({
  currentImage,
  imageRef,
  isCropping,
  crop,
  setCrop,
  setCompletedCrop,
  // completedCrop,
  cropAspect,
  isApplyingCrop,
  isResetting,
  hasBeenCropped,
  onStartCropping,
  onCancelCrop,
  onApplyCrop,
  onSetCropAspectRatio,
  onResetCrop,
}) => {
  return (
    <Box
      bg="background.secondary"
      borderRadius="xl"
      p={5}
      border="1px solid"
      borderColor="border.default"
    >
      {/* Header Row */}
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="text.secondary"
        textTransform="uppercase"
        letterSpacing="wider"
        fontFamily="secondary"
        mb={3}
      >
        Preview
      </Text>

      {/* Crop Controls Row */}
      <Flex justify="space-between" align="center" mb={4}>
        {/* Crop Button - Left Side */}
        <Button
          leftIcon={<CropIcon size={14} />}
          size="sm"
          bg={isCropping ? 'brand.primary.DEFAULT' : 'background.tertiary'}
          color={isCropping ? 'white' : 'text.primary'}
          border="1px solid"
          borderColor={isCropping ? 'brand.primary.DEFAULT' : 'border.default'}
          _hover={{ bg: isCropping ? 'brand.primary.hover' : 'border.default' }}
          onClick={() => (isCropping ? onCancelCrop() : onStartCropping())}
          fontFamily="secondary"
        >
          Crop
        </Button>

        {/* Aspect Ratio Buttons - Right Side, only shown when cropping */}
        {isCropping ? (
          <HStack spacing={2}>
            <HStack spacing={1}>
              {ASPECT_RATIOS.map(({ label, value }) => (
                <Button
                  key={label}
                  size="sm"
                  px={3}
                  bg={cropAspect === value ? 'brand.primary.DEFAULT' : 'transparent'}
                  color={cropAspect === value ? 'white' : 'text.secondary'}
                  border="1px solid"
                  borderColor="border.default"
                  _hover={{
                    bg: cropAspect === value ? 'brand.primary.hover' : 'background.tertiary',
                  }}
                  onClick={() => onSetCropAspectRatio(value)}
                  fontFamily="secondary"
                >
                  {label}
                </Button>
              ))}
            </HStack>
            <Button
              size="sm"
              leftIcon={!isApplyingCrop ? <Check size={14} /> : undefined}
              bg="status.success"
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={onApplyCrop}
              isLoading={isApplyingCrop}
              loadingText="Applying..."
              isDisabled={isApplyingCrop}
              fontFamily="secondary"
            >
              Apply
            </Button>

            <Button
              size="sm"
              leftIcon={<X size={14} />}
              variant="ghost"
              color="text.secondary"
              _hover={{ bg: 'background.tertiary' }}
              onClick={onCancelCrop}
              isDisabled={isApplyingCrop}
              fontFamily="secondary"
            >
              Cancel
            </Button>
          </HStack>
        ) : (
          <Button
            size="sm"
            leftIcon={!isResetting ? <RotateCcw size={14} /> : undefined}
            variant="ghost"
            color="text.secondary"
            _hover={{ bg: 'background.tertiary' }}
            onClick={onResetCrop}
            isLoading={isResetting}
            loadingText="Resetting..."
            isDisabled={!hasBeenCropped || isResetting}
            fontFamily="secondary"
          >
            Reset
          </Button>
        )}
      </Flex>

      {/* Image Display */}
      <Center minH="350px" bg="background.tertiary" borderRadius="lg" p={4}>
        {isCropping ? (
          <Box
            maxH="400px"
            w="100%"
            overflow="hidden"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={setCompletedCrop}
              aspect={cropAspect}
            >
              <ChakraImage
                ref={imageRef}
                src={currentImage.originalUrl}
                maxH="400px !important"
                maxW="100%"
                objectFit="contain"
                borderRadius="md"
              />
            </ReactCrop>
          </Box>
        ) : (
          <ChakraImage
            ref={imageRef}
            src={currentImage.originalUrl}
            maxH="400px"
            maxW="100%"
            objectFit="contain"
            borderRadius="md"
          />
        )}
      </Center>
    </Box>
  );
};
